import { Controller, Control, FieldValues, Path } from 'react-hook-form'
import DatePicker, { DateObject } from 'react-multi-date-picker'
import TimePicker from 'react-multi-date-picker/plugins/time_picker'
import { gregorian_fr } from '../../i18n/datepicker/fr'
import { cn } from '@onclicks/sharedcomponents/ui'
import { useTranslations } from 'next-intl'
import Icon from '../shared/ui/icon'
import { pickerDateToUtcIso, utcIsoToPickerDate } from '../../utils/picker-datetime'

type DateTimeFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  titre: string
  rules?: object
  error?: string
  description?: string
  containerClassName?: string
  className?: string
  minDate?: Date
  disabled?: boolean
  /** Ajoute un bouton qui efface la date (valeur `null`). */
  clearable?: boolean
  clearLabel?: string
}

type DateTimeInputProps = {
  /** Instant en ISO UTC, ou vide. Le picker l'affiche à l'heure de Bruxelles. */
  value?: string | null
  /** Rend l'instant choisi en ISO UTC, ou `''` quand la date est effacée : le back exige l'UTC. */
  onChange: (value: string) => void
  error?: string
  className?: string
  minDate?: Date
  disabled?: boolean
}

/**
 * Sélecteur date + heure sans label ni react-hook-form, pour les formulaires en état local.
 * `DateTimeField` l'enveloppe pour ceux qui passent par un `control`.
 */
export const DateTimeInput = ({ value, onChange, error, className, minDate, disabled }: DateTimeInputProps) => {
  const tCommon = useTranslations('common')

  return (
    <DatePicker
      value={value ? utcIsoToPickerDate(value) : null}
      onChange={(date: DateObject | null) => {
        onChange(date ? pickerDateToUtcIso(date) : '')
      }}
      format="DD-MM-YYYY HH:mm"
      className="text-black"
      locale={gregorian_fr}
      minDate={minDate ?? new Date()}
      disabled={disabled}
      plugins={[<TimePicker key="time" position="right" hStep={1} mStep={1} hideSeconds />]}
      containerClassName="w-full"
      // Le calendrier vit dans le body : dans le formulaire, il serait rogné par
      // l'overflow des sections, des modales et des tiroirs. Le z-index passe
      // au-dessus des plus hauts (z-[999]).
      portal
      zIndex={1000}
      inputClass={cn(
        'w-full bg-slate-50 rounded-lg p-3 border border-slate-200 dark:text-slate-800',
        'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary',
        error && 'ring-2 ring-red-500',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
      placeholder={tCommon('date_placeholder')}
    />
  )
}

const DateTimeField = <T extends FieldValues>(props: DateTimeFieldProps<T>) => {
  const {
    control,
    name,
    titre,
    rules,
    error,
    description,
    containerClassName,
    className,
    minDate,
    disabled,
    clearable,
    clearLabel,
  } = props

  return (
    <div className={cn('space-y-2', containerClassName)}>
      <label className="text-sm font-semibold text-slate-600 dark:text-white">{titre}</label>

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <DateTimeInput
              value={field.value}
              onChange={field.onChange}
              error={error}
              className={className}
              minDate={minDate}
              disabled={disabled}
            />
            {clearable && field.value && !disabled && (
              <button
                type="button"
                onClick={() => field.onChange(null)}
                title={clearLabel}
                aria-label={clearLabel}
                className="shrink-0 rounded-full p-2 text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <Icon name="close" className="text-xl" />
              </button>
            )}
          </div>
        )}
      />

      {description && !error && <p className="text-xs text-slate-500 italic">{description}</p>}

      {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
    </div>
  )
}

export default DateTimeField
